import {
  queryKeys,
  type StaffNotificationFeedItem,
  staffNotificationFeedResponseSchema,
} from '@mevaser/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  type AlertSoundKind,
  armAlertSound,
  playAlertSound,
  SOUND_LABELS,
} from '../lib/alert-sound.js'
import { api } from '../lib/api.js'

const POLL_MS = 10_000
const LAST_SEEN_KEY = 'mv-notification-sounds-last-seen'
const CLAIM_KEY = 'mv-notification-sounds-claim'
const tabId = Math.random().toString(36).slice(2)

function isKind(type: string): type is AlertSoundKind {
  return type !== 'ticket' && type in SOUND_LABELS
}

/**
 * Which feed items should make a sound: newer than `lastSeen`, of a known
 * non-ticket type (new tickets have their own chime). Newest first in, so the
 * first match is the one to play. A null `lastSeen` is the first look: quiet.
 */
export function newSoundItems(
  items: StaffNotificationFeedItem[],
  lastSeen: string | null,
): StaffNotificationFeedItem[] {
  if (lastSeen === null) return []
  return items.filter((i) => i.createdAt > lastSeen && isKind(i.type))
}

function loadLastSeen(): string | null {
  try {
    return sessionStorage.getItem(LAST_SEEN_KEY)
  } catch {
    return null
  }
}

function saveLastSeen(value: string): void {
  try {
    sessionStorage.setItem(LAST_SEEN_KEY, value)
  } catch {
    // A reload simply starts quiet.
  }
}

/** One sound per notification across all open tabs. */
function claim(key: string): boolean {
  try {
    const mine = `${key}|${tabId}`
    const existing = localStorage.getItem(CLAIM_KEY)
    if (existing?.startsWith(`${key}|`) && existing !== mine) return false
    localStorage.setItem(CLAIM_KEY, mine)
    return localStorage.getItem(CLAIM_KEY) === mine
  } catch {
    return true
  }
}

/** Plays the per-type sound when a non-ticket notification reaches the staff
 *  feed (t1411). Renders nothing; mounted once in `AppShell`. */
export function NotificationSounds() {
  const queryClient = useQueryClient()
  const lastSeen = useRef<string | null>(loadLastSeen())

  const { data } = useQuery({
    queryKey: [...queryKeys.staffNotifications(), 'sounds'] as const,
    queryFn: () =>
      api.get('/me/notifications', {
        query: { limit: 20 },
        schema: staffNotificationFeedResponseSchema,
      }),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  useEffect(() => {
    if (!data) return
    const fresh = newSoundItems(data.items, lastSeen.current)
    const newest = data.items.reduce<string | null>(
      (max, i) => (max === null || i.createdAt > max ? i.createdAt : max),
      lastSeen.current,
    )
    const first = fresh[0]
    if (first && isKind(first.type) && claim(`${first.id}:${first.createdAt}`)) {
      void playAlertSound(first.type)
    }
    if (fresh.length > 0) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.staffNotifications() })
    }
    // With an empty feed, remember "now" so the first arrival is not swallowed.
    const next = newest ?? new Date().toISOString()
    lastSeen.current = next
    saveLastSeen(next)
  }, [data, queryClient])

  useEffect(() => armAlertSound(), [])

  return null
}
